/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import { envUtil } from "dc-core";
import { getQueryParameterFromUrl } from "dc-preview-utils/src/Utils/UrlUtils";
import { getDevicePixelRatio } from "dc-preview-utils/src/Utils/DeviceUtils";
import * as constants from "constants/constants";
import * as enums from "constants/enums";
import { calculateEffectivePageWidth, calculateZoomLevel } from "./ZoomUtilsLite";

let sharedArrayBuffer = null;
let hasPreviewDropinInit = false;

export const isSABAvailable = () => window.SharedArrayBuffer && typeof window.SharedArrayBuffer === "function";

const getOriginTrialToken = () => {
  const hostEnv = envUtil.getHostEnv();
  const { localdev, dev, stage, prod } = constants.SHARED_ARRAY_BUFFER_ORIGIN_TRIAL_TOKENS;

  if (hostEnv === "localdev") {
    return localdev;
  } else if (hostEnv === "dev") {
    return dev;
  } else if (hostEnv === "stage") {
    return stage;
  } else if (hostEnv === "prod") {
    return prod;
  }
  return "";
};

/**
 * add SAB chrome origin-trial token in meta tag to enable SAB if SAB is not yet available
 * SAB: SharedArrayBuffer
 */
export const addSABOriginTrialMetaTag = () => {
  if (isSABAvailable()) return;
  const originTrialToken = getOriginTrialToken();
  const metaTag = document.createElement("meta");
  metaTag.httpEquiv = "origin-trial";
  metaTag.content = originTrialToken;
  document.head.appendChild(metaTag);
};

export const getSharedArrayBuffer = () => sharedArrayBuffer;

export const setSharedArrayBuffer = buffer => {
  sharedArrayBuffer = buffer;
};

export const getAssetUri = () => {
  let assetUri;
  const pathname = window.location.pathname.replace(/\/$/, "");
  if (/^\/id\//.test(pathname) || /^\/bench\/id\//.test(pathname)) {
    const splittedPathnames = pathname.split("/");
    assetUri = splittedPathnames[splittedPathnames.length - 1];
  } else {
    assetUri = getQueryParameterFromUrl(window.location.href, "uri");
  }
  if (assetUri) {
    assetUri = window.decodeURIComponent(assetUri);
  } else {
    assetUri = getQueryParameterFromUrl(window.location.href, "assetUri");
  }
  return assetUri;
};

export const addProviderPrefix = markerName => `preview_provider_${markerName}`;

export const recordProviderPerfMarker = markerName => {
  if (window.performance && window.performance.mark) {
    return window.performance.mark(addProviderPrefix(markerName));
  }
};

export const hasPageOrDocLevelActions = data => {
  let docLevelActions = false,
    pageJsActions = false,
    pageNonJsActions = false;
  // Currently stopping first page generation if any action is present
  // TODO : In future, can remove this or make it more lenient
  if (data && data.actions) {
    Object.keys(data.actions).forEach(key => {
      if (data.actions[key] && data.actions[key].length > 0) {
        docLevelActions = true;
      }
    });
  }
  if (data && data.pageLevelActions) {
    if (data.pageLevelActions && data.pageLevelActions.jsAction && data.pageLevelActions.jsAction.pageIndexes) {
      pageJsActions = data.pageLevelActions.jsAction.pageIndexes.length > 0;
    }
    if (data.pageLevelActions && data.pageLevelActions.nonJSAction && data.pageLevelActions.nonJSAction.pageIndexes) {
      pageNonJsActions = data.pageLevelActions.nonJSAction.pageIndexes.length > 0;
    }
  }
  return docLevelActions || pageJsActions || pageNonJsActions;
};

export const shouldCallGetRendition = data => {
  if (data.errorCode && data.errorCode !== enums.errorCodes.NO_ERROR) {
    return false;
  }
  if (
    data.isXFA ||
    data.isEncrypted ||
    data.isLinearizedWorkflow ||
    data.isPortfolio ||
    hasPageOrDocLevelActions(data)
  ) {
    return false;
  }
  if (data.pages && data.pages.length > 0) {
    return true;
  }
  return false;
};

export const getRenditionArgs = (mimeType, data, options) => {
  const fileShareType = options.fileShareType || "REVIEW";
  const showAnnots = fileShareType === "TRACK" ? 4293918719 : 4294485246;
  const effectivePageWidth = calculateEffectivePageWidth(options);
  const enableMetaDataAndRenditionAPI = options?.enableMetaDataAndRenditionAPI;
  return {
    pageNum: 1,
    ...(!enableMetaDataAndRenditionAPI && { zoomLevel: calculateZoomLevel(data, options) }),
    viewState: {
      pagesInView: {
        start: 1,
        end: 1
      },
      ...(!enableMetaDataAndRenditionAPI && { zoomLevel: calculateZoomLevel(data, options) }),
      cacheWindow: [], // TODO : Check
      allowDiffZoomlevelPages: false // Always false as true only in full screen mode
    },
    ...(enableMetaDataAndRenditionAPI && {
      renderRect: {
        width: effectivePageWidth
      }
    }),
    ...(enableMetaDataAndRenditionAPI && { renditionContainerWidth: options?.renditionContainerWidth }),
    devicePixelRatio: getDevicePixelRatio(),
    showAnnots, // TODO : Add code for _setSelectivelyRenderAnnotFlag
    hiddenOCGs: [], // TODO: check
    requestType: "page",
    priority: 10,
    isPreset: false, // True only in single page mode or in place mode or thumbnail
    size: constants.DEFAULT_PAGE_SIZE,
    format: "jpg",
    includeActions: false, // TODO : Currently we have disabled first page generation if any action is present
    enableOverprintSupport: false, // TODO: Check
    tileRect: {}, // Assuming no tiling
    commandName: "getPage",
    useAJS: true,
    useDCAPI: false,
    enableExternal: false,
    assetMimeType: mimeType,
    assetType: "/external",
    shouldCacheAJSRendition: true,
    colorPallete: null // TODO : init HC provider and get color palette. Send this param only if color palette is null.
  };
};

export const getHasPreviewDropinInit = () => hasPreviewDropinInit;

export const setHasPreviewDropinInit = value => {
  hasPreviewDropinInit = value;
};
